import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Heart, Minus, Plus, Star, Truck, RefreshCw, Ruler, ShoppingBag, ChevronRight } from "lucide-react";
import api, { eur, apiErr } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useWishlist } from "@/context/WishlistContext";
import { useAuth } from "@/context/AuthContext";
import ProductCard from "@/components/ProductCard";
import Seo from "@/components/Seo";
import { toast } from "sonner";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

export default function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const { has, toggle } = useWishlist();
  const { user } = useAuth();

  const [product, setProduct] = useState(null);
  const [color, setColor] = useState("");
  const [size, setSize] = useState("");
  const [qty, setQty] = useState(1);
  const [mainImg, setMainImg] = useState(0);
  const [zoom, setZoom] = useState({ show: false, x: 0, y: 0 });
  const [reviews, setReviews] = useState([]);
  const [newRating, setNewRating] = useState(5);
  const [newText, setNewText] = useState("");

  const load = useCallback(async () => {
    const { data } = await api.get(`/products/${slug}`);
    setProduct(data);
    setColor(data.colors?.[0] || "");
    setSize("");
    setMainImg(0);
    const { data: revs } = await api.get(`/products/${data.id}/reviews`);
    setReviews(revs);
  }, [slug]);

  useEffect(() => { window.scrollTo(0, 0); load(); }, [load]);

  if (!product) return <div className="min-h-[60vh] flex items-center justify-center"><div className="w-8 h-8 border-2 border-[#FF2E4C] border-t-transparent rounded-full animate-spin" /></div>;

  const onSale = product.sale_price != null;
  const price = onSale ? product.sale_price : product.price;
  const images = product.images?.length ? product.images : [product.image_front, product.image_back].filter(Boolean);
  const variantStock = (c, s) => product.variants?.find((v) => v.color === c && v.size === s)?.stock ?? 0;
  const selectedStock = size ? variantStock(color, size) : null;

  const addToCart = () => {
    if (!size) { toast.error("Επίλεξε μέγεθος"); return; }
    const stock = variantStock(color, size);
    if (stock <= 0) { toast.error("Εξαντλήθηκε"); return; }
    addItem({ product_id: product.id, slug: product.slug, name: product.name, color, size, price, quantity: qty, image: product.image_front, max_stock: stock });
  };
  const buyNow = () => { if (!size) { toast.error("Επίλεξε μέγεθος"); return; } addToCart(); navigate("/checkout"); };

  const submitReview = async () => {
    try {
      await api.post("/reviews", { product_id: product.id, rating: newRating, text: newText });
      toast.success("Η αξιολόγηση καταχωρήθηκε!");
      setNewText("");
      const { data: revs } = await api.get(`/products/${product.id}/reviews`);
      setReviews(revs);
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="pb-28 lg:pb-0">
      <Seo
        title={product.name}
        description={product.description?.slice(0, 160)}
        image={product.image_front}
        jsonLd={{
          "@context": "https://schema.org", "@type": "Product", name: product.name,
          image: images, description: product.description,
          brand: { "@type": "Brand", name: "SKGWEAR" },
          aggregateRating: product.reviews_count ? { "@type": "AggregateRating", ratingValue: product.rating, reviewCount: product.reviews_count } : undefined,
          offers: { "@type": "Offer", priceCurrency: "EUR", price: price,
            availability: (product.total_stock ?? 0) > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock" },
        }}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-4 flex items-center gap-1.5 text-xs font-mono text-zinc-500">
        <Link to="/" className="hover:text-white">HOME</Link><ChevronRight className="w-3 h-3" />
        <Link to="/shop" className="hover:text-white">SHOP</Link><ChevronRight className="w-3 h-3" />
        <span className="text-zinc-300 truncate">{product.name}</span>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16">
        {/* GALLERY */}
        <div>
          <div
            className="relative aspect-[3/4] overflow-hidden rounded-lg bg-[#18181C] cursor-zoom-in"
            onMouseMove={(e) => { const r = e.currentTarget.getBoundingClientRect(); setZoom({ show: true, x: ((e.clientX - r.left) / r.width) * 100, y: ((e.clientY - r.top) / r.height) * 100 }); }}
            onMouseLeave={() => setZoom({ show: false, x: 0, y: 0 })}
            data-testid="pdp-main-image"
          >
            <img src={images[mainImg]} alt={product.name} className="w-full h-full object-cover" style={zoom.show ? { transform: "scale(2)", transformOrigin: `${zoom.x}% ${zoom.y}%` } : {}} />
            {product.badge && <span className="absolute top-4 left-4 bg-[#FF2E4C] text-white px-3 py-1 text-xs font-mono font-bold tracking-widest uppercase">{product.badge}</span>}
          </div>
          {images.length > 1 && (
            <div className="flex gap-3 mt-3">
              {images.map((img, i) => (
                <button key={i} onClick={() => setMainImg(i)} className={`w-20 h-24 rounded overflow-hidden border-2 ${mainImg === i ? "border-[#FF2E4C]" : "border-transparent"}`} data-testid={`pdp-thumb-${i}`}>
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* INFO */}
        <div>
          <p className="font-mono text-[11px] tracking-[0.3em] uppercase text-zinc-500 mb-2">{product.category}</p>
          <h1 className="font-display text-3xl sm:text-4xl font-black uppercase tracking-tight leading-none">{product.name}</h1>
          <div className="flex items-center gap-2 mt-3">
            <div className="flex gap-0.5">{Array.from({ length: 5 }).map((_, i) => <Star key={i} className={`w-4 h-4 ${i < Math.round(product.rating) ? "fill-[#FF2E4C] text-[#FF2E4C]" : "text-zinc-600"}`} />)}</div>
            <span className="text-xs text-zinc-500 font-mono">{product.rating} ({product.reviews_count} reviews)</span>
          </div>

          <div className="flex items-center gap-3 mt-5">
            <span className={`text-3xl font-display font-black ${onSale ? "text-[#FF2E4C]" : "text-white"}`}>{eur(price)}</span>
            {onSale && <><span className="text-xl text-zinc-500 line-through">{eur(product.price)}</span><span className="bg-red-600 text-white text-xs font-bold px-2 py-1 rounded">-{Math.round((1 - price / product.price) * 100)}%</span></>}
          </div>

          {/* COLOR */}
          <div className="mt-7">
            <p className="font-display font-bold text-sm uppercase tracking-tight mb-3">Χρώμα: <span className="text-zinc-400 font-normal">{color}</span></p>
            <div className="flex flex-wrap gap-2">
              {product.colors?.map((c) => (
                <button key={c} onClick={() => { setColor(c); setSize(""); }} className={`px-4 py-2 text-sm border rounded ${color === c ? "bg-white text-black border-white" : "border-zinc-700 text-zinc-300 hover:border-white"}`} data-testid={`pdp-color-${c}`}>{c}</button>
              ))}
            </div>
          </div>

          {/* SIZE */}
          <div className="mt-6">
            <div className="flex items-center justify-between mb-3">
              <p className="font-display font-bold text-sm uppercase tracking-tight">Μέγεθος</p>
              <Dialog>
                <DialogTrigger asChild><button className="flex items-center gap-1 text-xs text-zinc-400 hover:text-[#FF2E4C]" data-testid="size-guide-trigger"><Ruler className="w-3.5 h-3.5" /> ΟΔΗΓΟΣ ΜΕΓΕΘΩΝ</button></DialogTrigger>
                <DialogContent className="bg-[#121215] border-zinc-800 text-white">
                  <DialogHeader><DialogTitle className="font-display uppercase">Οδηγός Μεγεθών</DialogTitle></DialogHeader>
                  <table className="w-full text-sm mt-2">
                    <thead><tr className="text-zinc-500 font-mono text-xs"><th className="text-left py-2">SIZE</th><th>Στήθος (cm)</th><th>Μήκος (cm)</th></tr></thead>
                    <tbody className="divide-y divide-zinc-800">
                      {[["S", "48", "68"], ["M", "51", "71"], ["L", "54", "74"], ["XL", "57", "76"], ["XXL", "60", "78"]].map((r) => <tr key={r[0]}><td className="py-2 font-bold">{r[0]}</td><td className="text-center">{r[1]}</td><td className="text-center">{r[2]}</td></tr>)}
                    </tbody>
                  </table>
                </DialogContent>
              </Dialog>
            </div>
            <div className="flex flex-wrap gap-2">
              {product.sizes?.map((s) => {
                const stock = variantStock(color, s);
                const out = stock <= 0;
                return (
                  <button key={s} disabled={out} onClick={() => setSize(s)} data-testid={`pdp-size-picker-${s}`}
                    className={`relative min-w-[52px] px-3 py-2.5 text-sm font-mono border rounded transition-colors ${
                      size === s ? "bg-[#FF2E4C] border-[#FF2E4C] text-white" : out ? "border-zinc-800 text-zinc-700 line-through cursor-not-allowed" : "border-zinc-700 text-zinc-200 hover:border-white"
                    }`}>
                    {s}
                  </button>
                );
              })}
            </div>
            {size && selectedStock != null && (
              selectedStock <= 0 ? <p className="text-sm text-zinc-500 mt-2 font-mono uppercase">Εξαντλήθηκε</p>
              : selectedStock <= 5 ? <p className="text-sm text-[#FF2E4C] mt-2 font-mono uppercase">Τελευταία κομμάτια · Μόνο {selectedStock} έμειναν</p>
              : <p className="text-sm text-green-500 mt-2 font-mono uppercase">Διαθέσιμο</p>
            )}
          </div>

          {/* QTY + ACTIONS */}
          <div className="flex items-center gap-3 mt-7">
            <div className="flex items-center border border-zinc-700 rounded">
              <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="p-3 hover:text-[#FF2E4C]" data-testid="pdp-quantity-minus"><Minus className="w-4 h-4" /></button>
              <span className="px-4 w-12 text-center" data-testid="pdp-quantity">{qty}</span>
              <button onClick={() => setQty((q) => q + 1)} className="p-3 hover:text-[#FF2E4C]" data-testid="pdp-quantity-plus"><Plus className="w-4 h-4" /></button>
            </div>
            <button onClick={() => toggle(product.id, product.name)} className="p-3 border border-zinc-700 rounded hover:border-[#FF2E4C]" data-testid="pdp-wishlist-button">
              <Heart className={`w-5 h-5 ${has(product.id) ? "fill-[#FF2E4C] text-[#FF2E4C]" : ""}`} />
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
            <button onClick={addToCart} className="btn-red py-4 text-sm font-bold tracking-widest uppercase rounded flex items-center justify-center gap-2" data-testid="pdp-add-to-cart-button"><ShoppingBag className="w-4 h-4" /> ΠΡΟΣΘΗΚΗ ΣΤΟ ΚΑΛΑΘΙ</button>
            <button onClick={buyNow} className="py-4 text-sm font-bold tracking-widest uppercase rounded bg-white text-black hover:bg-zinc-200 transition-colors" data-testid="pdp-buy-now-button">ΑΓΟΡΑ ΤΩΡΑ</button>
          </div>

          <div className="flex gap-6 mt-6 text-xs text-zinc-400">
            <span className="flex items-center gap-1.5"><Truck className="w-4 h-4 text-[#FF2E4C]" /> Δωρεάν μεταφορικά €80+</span>
            <span className="flex items-center gap-1.5"><RefreshCw className="w-4 h-4 text-[#FF2E4C]" /> 14 ημέρες επιστροφή</span>
          </div>

          {/* ACCORDION */}
          <Accordion type="single" collapsible className="mt-8" defaultValue="desc">
            <AccordionItem value="desc" className="border-zinc-800"><AccordionTrigger className="font-display uppercase text-sm tracking-tight">Περιγραφή</AccordionTrigger><AccordionContent className="text-zinc-400 text-sm leading-relaxed">{product.description}</AccordionContent></AccordionItem>
            <AccordionItem value="mat" className="border-zinc-800"><AccordionTrigger className="font-display uppercase text-sm tracking-tight">Υλικά</AccordionTrigger><AccordionContent className="text-zinc-400 text-sm leading-relaxed">{product.materials}</AccordionContent></AccordionItem>
            <AccordionItem value="dtf" className="border-zinc-800"><AccordionTrigger className="font-display uppercase text-sm tracking-tight">DTF Print Info</AccordionTrigger><AccordionContent className="text-zinc-400 text-sm leading-relaxed">{product.dtf_info}</AccordionContent></AccordionItem>
            <AccordionItem value="ship" className="border-zinc-800"><AccordionTrigger className="font-display uppercase text-sm tracking-tight">Αποστολή & Επιστροφές</AccordionTrigger><AccordionContent className="text-zinc-400 text-sm leading-relaxed">Αποστολή σε 2-4 εργάσιμες σε όλη την Ελλάδα. Express delivery διαθέσιμο. Δωρεάν επιστροφή εντός 14 ημερών.</AccordionContent></AccordionItem>
          </Accordion>
        </div>
      </div>

      {/* REVIEWS */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 mt-16">
        <h2 className="font-display text-2xl font-black uppercase tracking-tight mb-6">Αξιολογήσεις ({reviews.length})</h2>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-4">
            {reviews.length === 0 && <p className="text-zinc-500 text-sm">Δεν υπάρχουν αξιολογήσεις ακόμα. Γίνε ο πρώτος!</p>}
            {reviews.map((r) => (
              <div key={r.id} className="card-surface rounded-lg p-5">
                <div className="flex items-center justify-between">
                  <div className="flex gap-0.5">{Array.from({ length: 5 }).map((_, i) => <Star key={i} className={`w-3.5 h-3.5 ${i < r.rating ? "fill-[#FF2E4C] text-[#FF2E4C]" : "text-zinc-600"}`} />)}</div>
                  {r.verified_purchase && <span className="text-[10px] font-mono text-green-500 uppercase">✓ Επιβεβαιωμένη αγορά</span>}
                </div>
                <p className="text-zinc-300 text-sm mt-2">{r.text}</p>
                <p className="font-display font-bold text-xs uppercase mt-2">{r.user_name}</p>
              </div>
            ))}
          </div>
          <div className="card-surface rounded-lg p-5 h-fit">
            <h3 className="font-display font-bold uppercase text-sm mb-3">Γράψε αξιολόγηση</h3>
            {user ? (
              <>
                <div className="flex gap-1 mb-3">{Array.from({ length: 5 }).map((_, i) => <button key={i} onClick={() => setNewRating(i + 1)}><Star className={`w-6 h-6 ${i < newRating ? "fill-[#FF2E4C] text-[#FF2E4C]" : "text-zinc-600"}`} /></button>)}</div>
                <textarea value={newText} onChange={(e) => setNewText(e.target.value)} rows={4} placeholder="Η γνώμη σου..." className="w-full bg-[#0A0A0B] border border-zinc-700 rounded p-3 text-sm outline-none focus:border-[#FF2E4C]" data-testid="review-text" />
                <button onClick={submitReview} disabled={!newText.trim()} className="btn-red w-full mt-3 py-2.5 text-xs font-bold tracking-widest uppercase rounded disabled:opacity-40" data-testid="review-submit">ΥΠΟΒΟΛΗ</button>
              </>
            ) : (
              <p className="text-zinc-400 text-sm"><Link to="/login" className="text-[#FF2E4C] underline">Συνδέσου</Link> για να αφήσεις αξιολόγηση.</p>
            )}
          </div>
        </div>
      </div>

      {/* RELATED */}
      {product.related?.length > 0 && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 mt-16">
          <h2 className="font-display text-2xl font-black uppercase tracking-tight mb-6">Σχετικά προϊόντα</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">{product.related.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}</div>
        </div>
      )}

      {/* STICKY MOBILE BAR */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-40 glass border-t border-zinc-800 p-3 flex items-center gap-3" data-testid="pdp-sticky-add-to-cart-mobile">
        <div className="flex-1 min-w-0">
          <p className="font-display font-bold text-xs uppercase truncate">{product.name}</p>
          <p className={`text-sm font-bold ${onSale ? "text-[#FF2E4C]" : ""}`}>{eur(price)}</p>
        </div>
        <button onClick={addToCart} className="btn-red px-6 py-3 text-xs font-bold tracking-widest uppercase rounded whitespace-nowrap">ΣΤΟ ΚΑΛΑΘΙ</button>
      </div>
    </div>
  );
}
